import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The desktop header menu element
 * @property {HTMLElement} headerRowTop - The top header row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * Manages the responsive and sticky site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** @type {number | null} */
  #menuDrawerHiddenWidth = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {number | null} */
  #timeout = null;

  /** @type {number | null} */
  #resizeFrame = null;

  /** @type {number | null} */
  #scrollFrame = null;

  /** @type {MediaQueryList} */
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  /** @constant {number} */
  #animationDelay = 150;

  /**
   * Keeps the shared header-height variable current and restores the desktop
   * navigation once there is enough width after an overflow fallback.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const blockSize = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
    const roundedHeight = Math.round(blockSize);

    if (roundedHeight > 0) {
      document.body.style.setProperty('--header-height', `${roundedHeight}px`);
    }

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /** Update the global header height only for explicit browser lifecycle changes. */
  #syncHeaderHeight = () => {
    if (this.#resizeFrame) cancelAnimationFrame(this.#resizeFrame);

    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = null;
      const height = Math.round(this.getBoundingClientRect().height);
      if (height > 0) document.body.style.setProperty('--header-height', `${height}px`);
    });
  };

  /**
   * Observe the header position so sticky states can be applied reliably.
   * @param {boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (alwaysSticky) {
          this.dataset.stickyState = entry.isIntersecting
            ? 'inactive'
            : 'active';

          changeMetaThemeColor(this.refs.headerRowTop);
        } else {
          this.#offscreen =
            !entry.isIntersecting ||
            this.dataset.stickyState === 'active';
        }
      },
      {
        threshold: alwaysSticky ? 1 : 0,
      }
    );

    this.#intersectionObserver.observe(this);
  };

  /**
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Swap the desktop menu for the drawer when the desktop navigation overflows.
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
      return;
    }

    this.refs.headerDrawerContainer.classList.add('desktop:hidden');
    this.#menuDrawerHiddenWidth = null;
    this.refs.headerMenu.classList.remove('hidden');
  }

  /**
   * Clear temporary overflow classes whenever the native mobile/desktop
   * breakpoint changes. The overflow menu can immediately re-evaluate if the
   * available desktop width still requires the drawer.
   */
  #handleViewportChange = () => {
    this.refs.headerDrawerContainer.classList.add('desktop:hidden');
    this.refs.headerMenu.classList.remove('hidden');

    this.#menuDrawerHiddenWidth = null;
    this.removeAttribute('data-animating');

    if (!this.#desktopMediaQuery.matches) {
      this.dataset.scrollDirection = 'none';
    }

    this.#syncHeaderHeight();
  };

  /** Refresh dimensions when a page is restored from browser cache. */
  #handlePageShow = () => {
    this.#syncHeaderHeight();
  };

  #handleWindowScroll = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = this.getBoundingClientRect().top >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');

      this.#timeout = window.setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    this.#desktopMediaQuery.addEventListener(
      'change',
      this.#handleViewportChange
    );

    window.addEventListener(
      'pageshow',
      this.#handlePageShow
    );

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (
        stickyMode === 'scroll-up' ||
        stickyMode === 'always'
      ) {
        document.addEventListener(
          'scroll',
          this.#handleWindowScroll,
          { passive: true }
        );
      }
    }

    this.#syncHeaderHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();

    this.removeEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    this.#desktopMediaQuery.removeEventListener(
      'change',
      this.#handleViewportChange
    );

    window.removeEventListener(
      'pageshow',
      this.#handlePageShow
    );

    document.removeEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (this.#resizeFrame) {
      cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = null;
    }

    if (this.#scrollFrame !== null) {
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = null;
    }

    document.body.style.setProperty(
      '--header-height',
      '0px'
    );
  }
}

if (!customElements.get('header-component')) {
  customElements.define(
    'header-component',
    HeaderComponent
  );
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;

  const sizes = new Map();
  let lastHeight = null;

  const syncHeaderGroupHeight = () => {
    let totalHeight = 0;
    for (const height of sizes.values()) totalHeight += height;

    const roundedHeight = Math.round(totalHeight);
    if (roundedHeight === lastHeight) return;

    lastHeight = roundedHeight;
    document.body.style.setProperty('--header-group-height', `${roundedHeight}px`);
  };

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const blockSize = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      sizes.set(entry.target, Math.round(blockSize));
    }
    syncHeaderGroupHeight();
  });

  const observeChild = (element) => {
    if (!(element instanceof HTMLElement) || element === header || sizes.has(element)) return;
    sizes.set(element, Math.round(element.offsetHeight));
    resizeObserver.observe(element);
  };

  for (const element of headerGroup.children) observeChild(element);
  syncHeaderGroupHeight();

  // Header-group children are static on the storefront. Only the Theme Editor
  // inserts/removes sections dynamically, so avoid a permanent storefront
  // MutationObserver for work that cannot occur there.
  if (window.Shopify?.designMode) {
    const mutationObserver = new MutationObserver((mutations) => {
      let changed = false;

      for (const mutation of mutations) {
        for (const node of mutation.removedNodes) {
          if (!(node instanceof HTMLElement) || !sizes.has(node)) continue;
          resizeObserver.unobserve(node);
          sizes.delete(node);
          changed = true;
        }

        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement) || node === header || sizes.has(node)) continue;
          observeChild(node);
          changed = true;
        }
      }

      if (changed) syncHeaderGroupHeight();
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
